import React, { useState, useEffect } from "react";
import { 
  Button, 
  Card, 
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Box,
  CircularProgress,
  Tooltip,
  Chip
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ShoppingBag, Pets, AdminPanelSettings, Visibility, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AddProductPopup from '../components/AddProductPopup';
import AddGatPopup from '../components/AddGatPopup';

const theme = createTheme({
  palette: {
    primary: {
      main: '#B3A6D2',
      contrastText: '#2A2252',
    },
    secondary: {
      main: '#6C5B7B',
    },
    error: {
      main: '#C44536',
    },
  },
  shape: {
    borderRadius: 4,
  },
});

export default function Dashboard() {
  const [view, setView] = useState("products");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [openProductPopup, setOpenProductPopup] = useState(false);
  const [openCatPopup, setOpenCatPopup] = useState(false);
  const navigate = useNavigate();
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCat, setEditingCat] = useState(null);

  useEffect(() => {
    fetch("http://localhost:5000/dashboard/data", {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("token")}`
      }
    })
      .then(response => {
        if (response.status === 403) {
          localStorage.removeItem("token");
          navigate("/admin");
        }
        return response.json();
      })
      .catch(error => {
        console.error("Erro na requisição:", error);
      });
  }, []);

  useEffect(() => {
    Promise.all([
      fetch("http://localhost:5000/products"),
      fetch("http://localhost:5000/cats"),
      fetch("http://localhost:5000/categories"),
    ])
    .then(async ([resProducts, resCats, resCategories]) => {
      setProducts(await resProducts.json());
      setCats(await resCats.json());
      setCategories(await resCategories.json());
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  }, []);

  const handleAddProduct = (productData) => {
    const formData = new FormData();
    
    Object.entries(productData).forEach(([key, value]) => {
      if (key !== 'images') formData.append(key, value);
    });

    productData.images.forEach((img, index) => {
      formData.append(`images`, img.file);
      formData.append(`isPrimary_${index}`, img.isPrimary ? '1' : '0');
    });

    fetch("http://localhost:5000/addProduct", {
      method: "POST",
      body: formData,
    }).then(() => {
      setOpenProductPopup(false);
      window.location.reload();
    });
  };

  const handleAddCat = (catData) => {
    const formData = new FormData();
    
    Object.entries(catData).forEach(([key, value]) => {
      if (key !== 'images') formData.append(key, value);
    });

    catData.images.forEach((img, index) => {
      formData.append(`images`, img.file);
      formData.append(`isPrimary_${index}`, img.isPrimary ? '1' : '0');
    });

    fetch("http://localhost:5000/addCat", {
      method: "POST",
      body: formData,
    }).then(() => {
      setOpenCatPopup(false);
      window.location.reload();
    });
  };

  const TableHeader = ({ view }) => (
    <TableHead>
      <TableRow>
        {view === "products" ? (
          ['ID', 'Nom', 'Preu', 'Accions'].map((header, index) => (
            <TableCell key={index} sx={{ 
              color: theme.palette.primary.dark,
              fontSize: '0.9rem',
              fontWeight: 600,
              py: 1.5,
              borderBottom: `2px solid ${theme.palette.primary.main}`
            }}>
              {header}
            </TableCell>
          ))
        ) : (
          ['ID', 'Nom', 'Raça', 'Accions'].map((header, index) => (
            <TableCell key={index} sx={{ 
              color: theme.palette.primary.dark,
              fontSize: '0.9rem',
              fontWeight: 600,
              py: 1.5,
              borderBottom: `2px solid ${theme.palette.primary.main}`
            }}>
              {header}
            </TableCell>
          ))
        )}
      </TableRow>
    </TableHead>
  );

  const handleDelete = (id) => {
    const url = view === "products" ? `/deleteProduct/${id}` : `/deleteCat/${id}`;
    fetch(`http://localhost:5000${url}`, {
      method: "DELETE",
    }).then(() => window.location.reload());
  };

  return (
    <ThemeProvider theme={theme}>
        <Button startIcon={<ShoppingBag />} onClick={() => setOpenProductPopup(true)}>
          Afegir Productes
        </Button>
        <Button startIcon={<Pets />} onClick={() => setOpenCatPopup(true)}>
          Afegir Gat  
        </Button>

        <AddProductPopup
          open={openProductPopup}
          onClose={() => {
            setOpenProductPopup(false);
            setEditingProduct(null);
          }}
          categories={categories}
          onSubmit={handleAddProduct}
          initialData={editingProduct}
        />

        <AddGatPopup
          open={openCatPopup}
          onClose={() => {
            setOpenCatPopup(false);
            setEditingCat(null);
          }}
          onSubmit={handleAddCat}
          initialData={editingCat}
        />

        <Container maxWidth="xl" sx={{ 
          py: 4,
          background: '#FFFFFF',
          minHeight: '100vh'
        }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4,
          p: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AdminPanelSettings sx={{ 
              fontSize: 32,
              color: theme.palette.primary.main 
            }}/>
            <Typography variant="h5" component="h1" sx={{ 
              fontWeight: 600,
              color: theme.palette.primary.dark
            }}>
              Panell Administratiu
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant={view === "products" ? "contained" : "text"}
              onClick={() => setView("products")}
              startIcon={<ShoppingBag sx={{ fontSize: 20 }} />}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              Productes
            </Button>
            <Button
              variant={view === "cats" ? "contained" : "text"}
              onClick={() => setView("cats")}
              startIcon={<Pets sx={{ fontSize: 20 }} />}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
              color="secondary"
            >
              Gats
            </Button>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 10
          }}>
            <CircularProgress sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <Card elevation={0} sx={{ 
            border: `1px solid ${theme.palette.divider}`,
          }}>
            <TableContainer>
              <Table>
                <TableHeader view={view} />
                <TableBody>
                  {(view === "products" ? products : cats).map((item) => (
                    <TableRow 
                      key={item.id}
                      hover
                      sx={{ 
                        '&:last-child td': { border: 0 },
                      }}
                    >
                      <TableCell sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        {item.id}
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 500,
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}>
                        {item.name}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        {view === "products" 
                          ? `€ ${item.price.toFixed(2)}`
                          : <Chip 
                              label={item.breed} 
                              size="small" 
                              sx={{ 
                                backgroundColor: theme.palette.primary.main + '10',
                                color: theme.palette.primary.dark,
                                fontSize: '0.8rem'
                              }}
                            />}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Editar" arrow>
                            <IconButton
                              size="small"
                              sx={{ 
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: theme.palette.primary.main + '10' }
                              }}
                              onClick={async () => {
                                if (view === "products") {
                                  const res = await fetch(`http://localhost:5000/product/${item.id}`);
                                  const data = await res.json();
                                  setEditingProduct(data);
                                  setOpenProductPopup(true);
                                } else {
                                  const res = await fetch(`http://localhost:5000/cat/${item.id}`);
                                  const data = await res.json();
                                  setEditingCat(data);
                                  setOpenCatPopup(true);
                                }
                              }}
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar" arrow>
                          <IconButton onClick={() => handleDelete(item.id)}>
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {(products.length === 0 && cats.length === 0) && (
              <Box sx={{ 
                textAlign: 'center', 
                p: 4,
              }}>
                <Typography variant="body1" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                  {view === "products" 
                    ? "No hi ha productes disponibles" 
                    : "No hi ha gats registrats"}
                </Typography>
                <Pets sx={{ 
                  fontSize: 32, 
                  color: theme.palette.text.disabled 
                }}/>
              </Box>
            )}
          </Card>
        )}
      </Container>
    </ThemeProvider>
  )
}